import React from 'react';
import './PoseHud.css';

const PHASES = [
  { id: 'SETUP', label: '준비 (Setup)', shortLabel: '준비' },
  { id: 'DRAW', label: '당김 (Draw)', shortLabel: '당김' },
  { id: 'ANCHOR', label: '조준 (Anchor)', shortLabel: '조준' },
  { id: 'RELEASE', label: '발사 (Release)', shortLabel: '발사' },
];

export function PoseHud({
  fps,
  hasPoseDetected,
  isStreaming,
  isLoadingModel,
  angles,
  phaseData,
  isRecording,
  formattedDuration,
  isPaused,
  autoClipEnabled,
  autoClipStatus,
  shotCount = 0,
  dominantHand = 'right',
  onToggleDominantHand,
}) {
  if (!isStreaming) return null;

  const isLeft = dominantHand === 'left';

  return (
    <div className="pose-hud">
      <div className="pose-hud__top-bar">
        <div className="pose-hud__status-group">
          <div className="pose-hud__status-pill">
            <span>
              {isLoadingModel
                ? '로딩 중...'
                : isPaused
                ? '일시 정지됨'
                : `${fps} FPS`}
            </span>
          </div>

          <button
            type="button"
            className="pose-hud__hand-indicator"
            onClick={onToggleDominantHand}
            title={
              isLeft
                ? '현재: 왼쪽 기준 (11·13·15 당김, 12·14·16 받침) ➔ 클릭 시 오른쪽 기준으로 변경'
                : '현재: 오른쪽 기준 (12·14·16 당김, 11·13·15 받침) ➔ 클릭 시 왼쪽 기준으로 변경'
            }
          >
            <span>{isLeft ? '왼쪽 기준' : '오른쪽 기준'}</span>
          </button>

          {autoClipEnabled && (
            <div
              className={`pose-hud__autoclip-badge ${
                autoClipStatus === 'RECORDING'
                  ? 'pose-hud__autoclip-badge--rec'
                  : autoClipStatus === 'SAVED'
                  ? 'pose-hud__autoclip-badge--saved'
                  : autoClipStatus === 'ABORTED'
                  ? 'pose-hud__autoclip-badge--aborted'
                  : ''
              }`}
            >
              {autoClipStatus === 'RECORDING' && <span className="pose-hud__record-dot" />}
              <span>
                {autoClipStatus === 'RECORDING'
                  ? `Shot #${shotCount + 1} 녹화 중`
                  : autoClipStatus === 'SAVED'
                  ? `Shot #${shotCount} 저장 완료`
                  : autoClipStatus === 'ABORTED'
                  ? '사격 취소됨 (녹화 삭제)'
                  : `AI 자동 컷팅 (${shotCount}발 완료)`}
              </span>
            </div>
          )}

          {!autoClipEnabled && isRecording && (
            <div className="pose-hud__record-badge">
              <span className="pose-hud__record-dot" />
              <span>REC {formattedDuration || '00:00'}</span>
            </div>
          )}
        </div>

        <div className="pose-hud__phases">
          {PHASES.map((p) => (
            <span
              key={p.id}
              className={`pose-hud__phase-badge ${
                phaseData?.phase === p.id ? 'pose-hud__phase-badge--active' : ''
              }`}
            >
              <span className="pose-hud__phase-label-full">{p.label}</span>
              <span className="pose-hud__phase-label-short">{p.shortLabel}</span>
            </span>
          ))}
        </div>
      </div>

      {hasPoseDetected && (
        <div
          className="pose-hud__metrics-panel"
          aria-label="실시간 자세 각도 및 조준 타이머"
          tabIndex={0}
        >
          <div className="pose-hud__metric-box pose-hud__metric-box--support-arm">
            <div className="pose-hud__metric-label">
              <span className="pose-hud__color-dot pose-hud__color-dot--cyan" />
              <span className="pose-hud__metric-title">받침 팔</span>
              <span className="pose-hud__metric-sub">{isLeft ? '14-12-24' : '13-11-23'}</span>
            </div>
            <strong className="pose-hud__metric-value">{angles?.angleSupportArm ?? 0}°</strong>
          </div>

          <div className="pose-hud__metric-box pose-hud__metric-box--draw-arm">
            <div className="pose-hud__metric-label">
              <span className="pose-hud__color-dot pose-hud__color-dot--orange" />
              <span className="pose-hud__metric-title">당김 팔</span>
              <span className="pose-hud__metric-sub">{isLeft ? '13-11-23' : '14-12-24'}</span>
            </div>
            <strong className="pose-hud__metric-value">{angles?.angleDrawArm ?? 0}°</strong>
          </div>

          <div className="pose-hud__metric-box pose-hud__metric-box--anchor-angle">
            <div className="pose-hud__metric-label">
              <span className="pose-hud__color-dot pose-hud__color-dot--gold" />
              <span className="pose-hud__metric-title">앵커 각</span>
              <span className="pose-hud__metric-sub">{isLeft ? '11-13-15' : '12-14-16'}</span>
            </div>
            <strong className="pose-hud__metric-value">{angles?.drawElbowAngle || 0}°</strong>
          </div>

          <div className="pose-hud__metric-box pose-hud__metric-box--hold-angle">
            <div className="pose-hud__metric-label">
              <span className="pose-hud__color-dot pose-hud__color-dot--emerald" />
              <span className="pose-hud__metric-title">지탱 각</span>
              <span className="pose-hud__metric-sub">{isLeft ? '12-14-16' : '11-13-15'}</span>
            </div>
            <strong className="pose-hud__metric-value">{angles?.bowArmAngle || 0}°</strong>
          </div>

          <div className="pose-hud__metric-box pose-hud__metric-box--torso-angle">
            <div className="pose-hud__metric-label">
              <span className="pose-hud__color-dot pose-hud__color-dot--purple" />
              <span className="pose-hud__metric-title">상체 수직</span>
            </div>
            <strong className="pose-hud__metric-value">{angles?.torsoAngle || 90}°</strong>
          </div>

          <div className="pose-hud__metric-box pose-hud__metric-box--anchor-time">
            <div className="pose-hud__metric-label">
              <span className="pose-hud__color-dot pose-hud__color-dot--gold" />
              <span className="pose-hud__metric-title">조준 시간</span>
            </div>
            <strong className="pose-hud__metric-value">
              {phaseData?.anchorDuration > 0 ? `${phaseData.anchorDuration}s` : '-'}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
}
